/**
 * The "contains" filter as SQL — `LOWER(<text>) LIKE LOWER('%<value>%')` —
 * with the value matched literally, and its inverse for the reopen parser.
 * Every emitter that wraps a typed value in `%…%` writes it here: the scalar
 * `CONTAINS` operator in both filter emitters (the renderer's
 * `filterWhereBuilder.ts`, Table Explorer's `electron/table-explorer/
 * queryBuilder/queryUtils.ts`) and the JSON text sub-operators
 * (`path_contains`, `any_contains`, their wildcard / recursive forms in
 * `jsonFilterSql.ts`).
 *
 * Unescaped, `contains 50%` matched `500` and `contains a_b` matched `axb`:
 * `%` and `_` are LIKE wildcards on every engine, `[` opens a character class
 * on SQL Server (`%[a]%` matches any `a`), and PostgreSQL and MySQL read `\`
 * as LIKE's default escape (`contains a\b` matched `ab`).
 *
 * WHY THE ESCAPE CHARACTER IS `!` AND NOT A BACKSLASH
 * `!` is an ordinary character inside a string literal on all five engines in
 * every mode, no engine's LIKE gives it a meaning of its own, and `LOWER()`
 * leaves it alone — so `ESCAPE '!'` is the same text everywhere. A backslash
 * is not: MySQL has no quoted spelling of a one-character `\` that parses in
 * both SQL modes (`'\'` is unterminated by default, `'\\'` is two characters
 * under NO_BACKSLASH_ESCAPES) and every escaped pattern would need the
 * `_utf8mb4 X'…'` hex form, unreadable in the Visual view; and PostgreSQL with
 * `standard_conforming_strings = off` reads `'\'` as an escaped quote.
 *
 * WHAT IS ESCAPED, AND WHEN THE CLAUSE IS WRITTEN
 * `%`, `_` and `!` itself, and `[` on SQL Server only — Oracle rejects an
 * escape character followed by anything but `%`, `_` or itself (ORA-01424).
 * The `ESCAPE '!'` clause is written only when the value holds one of those or
 * a backslash (to switch off PostgreSQL's and MySQL's default escape). A value
 * with none keeps the short form the chip always wrote, so a saved filter
 * reopens and re-applies byte for byte.
 *
 * SQL SERVER: A VALUE OUTSIDE THE BMP
 * `quote` writes an emoji's pattern `N'%😀%' COLLATE Latin1_General_100_CI_AS_SC`
 * (`sqlServerTextLiteral.ts`), so the clause is
 * `LOWER(col) LIKE LOWER(N'%😀%' COLLATE …)`: under the usual database
 * collations a surrogate pair has no weight and `LIKE N'%😀%'` is `LIKE '%%'`,
 * every non-null row. The explicit collation inside `LOWER()` outranks the
 * column's.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

export const CONTAINS_ESCAPE_CLAUSE = "ESCAPE '!'";

/** The characters written with the `!` escape on `engine`. */
function escapedChars(engine: DatabaseEngineId): string {
  return engine === 'mssql' ? '%_![' : '%_!';
}

/** The characters that are wildcards when they are not escaped. */
function wildcardChars(engine: DatabaseEngineId): string {
  return engine === 'mssql' ? '%_[' : '%_';
}

/** Whether a contains value needs `ESCAPE '!'` on `engine` (see the header). */
export function containsValueNeedsEscape(value: string, engine: DatabaseEngineId): boolean {
  const chars = escapedChars(engine);
  return [...value].some((ch) => ch === '\\' || chars.includes(ch));
}

/**
 * `LOWER(<textExpr>) LIKE LOWER(<quoted %value%>)`, plus ` ESCAPE '!'` and the
 * value's metacharacters escaped when it has any. `quote` writes the pattern
 * as the engine's text literal (MySQL's hex form for a backslash, SQL Server's
 * `N'…'`, …).
 */
export function containsLikeSql(
  textExpr: string,
  value: unknown,
  engine: DatabaseEngineId,
  quote: (pattern: string) => string,
): string {
  const raw = String(value ?? '');
  if (!containsValueNeedsEscape(raw, engine)) return `LOWER(${textExpr}) LIKE LOWER(${quote(`%${raw}%`)})`;
  const chars = escapedChars(engine);
  const body = [...raw].map((ch) => (chars.includes(ch) ? `!${ch}` : ch)).join('');
  return `LOWER(${textExpr}) LIKE LOWER(${quote(`%${body}%`)}) ${CONTAINS_ESCAPE_CLAUSE}`;
}

/**
 * Regex source for the optional ESCAPE clause after a contains pattern,
 * capturing the clause: `ESCAPE '!'`, or the backslash clause the JSON filter
 * wrote before (`_utf8mb4 X'5c'` on MySQL), which saved queries still hold.
 */
export const CONTAINS_ESCAPE_SRC = `(?:\\s+(ESCAPE\\s+(?:'!'|'\\\\'|_utf8mb4\\s*[xX]'5[cC]')))?`;

/** `body` with `esc`-escaped characters read back; null on an unescaped wildcard or a stray escape. */
function unescapeBody(body: string, esc: string, escapable: string, wildcards: string): string | null {
  let out = '';
  for (let i = 0; i < body.length; i++) {
    const ch = body[i]!;
    if (ch === esc) {
      const next = body[i + 1];
      if (next === undefined || !escapable.includes(next)) return null;
      out += next;
      i += 1;
    } else if (wildcards.includes(ch)) {
      return null;
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * The value a contains pattern was written for. `pattern` is the decoded
 * literal (`%…%`), `escapeClause` the clause `CONTAINS_ESCAPE_SRC` captured.
 * `null` when the pattern is not one this module writes — a wildcard of its
 * own inside, or, without a clause, a character the emitter would have
 * escaped — so the predicate stays raw text and keeps its meaning.
 */
export function containsValueOfPattern(
  pattern: string,
  escapeClause: string | undefined,
  engine: DatabaseEngineId,
): string | null {
  if (pattern.length < 2 || !pattern.startsWith('%') || !pattern.endsWith('%')) return null;
  const body = pattern.slice(1, -1);
  if (!escapeClause) return containsValueNeedsEscape(body, engine) ? null : body;
  if (escapeClause.includes('!')) return unescapeBody(body, '!', escapedChars(engine), wildcardChars(engine));
  return unescapeBody(body, '\\', '\\%_', wildcardChars(engine));
}
